/**
 * On-demand reverse geocode for dwell place-name lookup.
 * Prefers Azure Maps Search when a subscription key is configured;
 * falls back to Mapbox (the token already used by Pathfinder map UI).
 */

export interface ReverseGeocodeResult {
  placeName: string;
  /** Free-form address / longer label when available. */
  address?: string;
  placeType?: string;
  source: 'azure' | 'mapbox';
}

function azureKey(): string {
  return (
    process.env.REACT_APP_AZURE_MAPS_SUBSCRIPTION_KEY ||
    process.env.REACT_APP_AZURE_MAPS_KEY ||
    ''
  ).trim();
}

function mapboxToken(): string {
  const fromEnv = (process.env.REACT_APP_MAPBOX_TOKEN || '').trim();
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && (window as any).MAPBOX_TOKEN) {
    return String((window as any).MAPBOX_TOKEN);
  }
  return '';
}

async function reverseViaAzure(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
  const key = azureKey();
  if (!key) return null;
  const url =
    `https://atlas.microsoft.com/search/address/reverse/json` +
    `?api-version=1.0&query=${lat},${lon}&subscription-key=${encodeURIComponent(key)}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const addr = data?.addresses?.[0]?.address;
  if (!addr) return null;
  const placeName =
    addr.freeformAddress ||
    [addr.streetName, addr.municipality, addr.countrySubdivisionName || addr.countrySubdivision]
      .filter(Boolean)
      .join(', ') ||
    addr.municipality ||
    '';
  if (!placeName) return null;
  return {
    placeName,
    address: addr.freeformAddress || placeName,
    placeType: addr.entityType || addr.localName || undefined,
    source: 'azure',
  };
}

async function reverseViaMapbox(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
  const token = mapboxToken();
  if (!token) return null;
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json` +
    `?types=poi,address,neighborhood,locality,place&limit=1&access_token=${encodeURIComponent(token)}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const feat = data?.features?.[0];
  if (!feat) return null;
  const placeName = feat.text || feat.place_name || '';
  if (!placeName) return null;
  const placeType = Array.isArray(feat.place_type) ? feat.place_type[0] : feat.place_type;
  return {
    placeName: feat.place_name || placeName,
    address: feat.place_name || placeName,
    placeType: typeof placeType === 'string' ? placeType : undefined,
    source: 'mapbox',
  };
}

/** Reverse-geocode a coordinate. Returns null if no provider is configured or lookup fails. */
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  try {
    const azure = await reverseViaAzure(lat, lon);
    if (azure) return azure;
  } catch {
    /* fall through */
  }
  try {
    return await reverseViaMapbox(lat, lon);
  } catch {
    return null;
  }
}

export function hasReverseGeocodeProvider(): boolean {
  return Boolean(azureKey() || mapboxToken());
}
